import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { DateAdapter, MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule, Sort, SortDirection } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CdkDrag, CdkDragDrop, CdkDropList, CdkDropListGroup, moveItemInArray } from '@angular/cdk/drag-drop';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { compararValoresOrdenacao } from '../../../shared/helpers/ordenacao-tabela.helper';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { EmptyListComponent } from '../../../shared/components/empty-list/empty-list.component';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { LoadingBoxComponent } from '../../../shared/components/loading-box/loading-box.component';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { PermissaoEnum } from '@shared/enums/usuario/permissao.enum';
import { TipoContatoEnum } from '@shared/enums/pessoa/tipo-contato.enum';
import { SituacaoVendedorEnum } from '@shared/enums/pessoa/situacao-vendedor.enum';
import { EscopoVisualizacaoEnum } from '@shared/enums/pessoa/escopo-visualizacao.enum';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { CrmEtapasKanbanService } from '../../../shared/services/apis/vendas/crm-etapas-kanban.service';
import { OportunidadeService } from '../../../shared/services/apis/vendas/oportunidade.service';
import { TagCrmService } from '../../../shared/services/apis/vendas/tag-crm.service';
import { PessoaLookupService } from '../../../shared/services/apis/pessoa/pessoa-lookup.service';
import { PessoaService } from '../../../shared/services/apis/pessoa/pessoa.service';
import { AuthService } from '../../../shared/services/apis/usuario/auth.service';
import { EnvolvidosEtapaService } from './envolvidos-etapa.service';
import {
  LookupOportunidade,
  OportunidadeAnexo,
  OportunidadeCampoValorItem,
  OportunidadeComentario,
  OportunidadeKanbanItem,
  TagOportunidade,
} from '../../../shared/services/models/vendas/oportunidade.model';
import { OportunidadeKanbanEtapaItemComValorStruct } from '@shared/services/structs/vendas/oportunidade-kanban-etapa-item-com-valor.struct';
import { Pessoa } from '../../../shared/services/models/pessoa/pessoa.model';
import { ContatoPessoaStruct } from '../../../shared/services/structs/pessoa/contato-pessoa.struct';
import { GetOportunidadesFiltroRequest } from '../../../shared/services/requests/vendas/get-oportunidades-filtro.request';
import { UsuarioService } from '../../../shared/services/apis/usuario/usuario.service';
import { UtilService } from '../../../shared/services/util.service';
import { CrmEtapaOportunidade } from '../../../shared/services/models/vendas/crm-etapa-oportunidade.model';
import { OrigemNovaOportunidade } from '@shared/services/models/vendas/origem-nova-oportunidade.model';
import { CustomDateAdapter } from '../../../shared/services/custom-date-adapter';
import { UsuarioListaStruct } from '../../../shared/services/structs/usuario/usuario-lista.struct';
import { VendedorLookupStruct } from '@shared/services/structs/pessoa/vendedor-lookup.struct';
import { VendaEscopoStruct } from '@shared/services/structs/vendas/venda-escopo.struct';
import { CrmCampoDefinicaoOpcaoDto } from '@shared/services/responses/vendas/crm-configuracao-etapa-kanban.response';

import {
  EncerrarOportunidadeDialogComponent,
  EncerrarOportunidadeResult,
} from './dialogs/encerrar-oportunidade-dialog/encerrar-oportunidade-dialog.component';
import {
  ProximaAcaoDialogComponent,
  ProximaAcaoResult,
} from './dialogs/proxima-acao-dialog/proxima-acao-dialog.component';
import { OportunidadeCardDialogComponent } from './dialogs/oportunidade-card-dialog/oportunidade-card-dialog.component';
import { OportunidadeCardDialogResult } from './dialogs/oportunidade-card-dialog/oportunidade-card-dialog.types';
import { TagsOportunidadeDialogComponent } from './dialogs/tags-oportunidade-dialog/tags-oportunidade-dialog.component';
import {
  NovaOportunidadeData,
  NovaOportunidadeDialogComponent,
  NovaOportunidadeResult,
} from './dialogs/nova-oportunidade-dialog/nova-oportunidade-dialog.component';
import {
  aplicarDetalhe,
  ContextoMapeamento,
  iconeDoValor,
  iniciaisDoNome,
  mapaDeLookup,
  mapearCard,
  origemDoValor,
} from './crm-oportunidades.mapper';
import {
  AVATAR_COLORS,
  CAMPO_DATA_OPCOES,
  CAMPO_DATA_PADRAO,
  CampoDataFiltro,
  FAIXA_VALOR_OPCOES,
  ID_POR_CAMPO_DATA,
  ID_POR_PRIORIDADE,
  OportunidadeCard,
  OportunidadeStage,
  PERIODO_OPCOES,
  PERIODO_PADRAO,
  PeriodoBoard,
  Person,
  PRIORIDADE_POR_CHAVE,
  PRIORIDADES,
  PrioridadeChave,
  PrioridadeInfo,
  corDoStatusCrm,
  STATUS_CLIENTE_INDEFINIDO,
  StatusClienteChave,
  StatusClienteInfo,
  VISAO_OPCOES,
  CONTADOR_SUBSTANTIVO,
  VisaoBoard,
} from './crm-oportunidades.models';

/** Paginação de uma coluna do Kanban. Mesma forma do processo-compras. */
interface EtapaPageState {
  paginaAtual: number;
  carregando: boolean;
  temMais: boolean;
  totalRegistros: number;
  somaValor: number;
  carregadaAlgumaVez: boolean;
  /** Descarta resposta de busca antiga que chegou depois de uma mais nova. */
  token: number;
}

const ESTADO_ETAPA_VAZIO: EtapaPageState = {
  paginaAtual: 0, carregando: false, temMais: false,
  totalRegistros: 0, somaValor: 0, carregadaAlgumaVez: false, token: 0,
};

const ETAPA_DESCONHECIDA: OportunidadeStage = {
  key: '',
  idEtapaKanban: 0,
  name: 'Sem etapa',
  descricao: '',
  icon: 'help',
  color: '#9AA0A6',
  tint: '#9AA0A622',
  ordem: 0,
  isEspecial: false,
  isFinal: false,
  idEtapaKanbanRetornoReabertura: null,
};

@Component({
  selector: 'app-crm-oportunidades',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatBadgeModule,
    MatButtonModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
    HeaderComponent,
    EmptyListComponent,
    LoadingBoxComponent,
    CdkDropList,
    CdkDropListGroup,
    CdkDrag,
  ],
  providers: [
    provideNativeDateAdapter(),
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
    // Parse de datas digitadas em DD/MM/AAAA — mesmo adapter do resto do sistema.
    { provide: DateAdapter, useClass: CustomDateAdapter },
  ],
  templateUrl: './crm-oportunidades.component.html',
  styleUrl: './crm-oportunidades.component.css',
})
export class CrmOportunidadesComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly moduloEnum = ModuloEnum.Vendas;
  readonly funcionalidadeEnum = FuncionalidadeEnum.Vendas_Crm;

  activeTab: 'kanban' | 'lista' = 'kanban';
  kanbanExpanded = false;

  @ViewChild('tabBtnKanban') private tabBtnKanban?: ElementRef<HTMLElement>;
  @ViewChild('tabBtnLista') private tabBtnLista?: ElementRef<HTMLElement>;
  indicadorX = 0;
  indicadorW = 0;

  readonly skeletonCards = [1, 2, 3];
  readonly skeletonColumns = [1, 2, 3, 4, 5];
  etapasCarregando = true;

  // ----- Etapas (configuração real) -----
  mainStages: OportunidadeStage[] = [];
  sideStages: OportunidadeStage[] = [];
  allStages: OportunidadeStage[] = [];
  collapsedStages = new Set<string>();

  /** origem -> destinos, vindo de etapa_kanban_movimentacao. */
  private transicoes: Record<string, string[]> = {};

  // ----- Dados -----
  oportunidadesCarregando = true;
  /** Catálogos carregados. Sem eles o card mostraria id cru no lugar do nome. */
  private contextoPronto = false;
  /**
   * Status de relacionamento por cliente (CA09). Indexado por idPessoa e vindo de
   * db_pessoa: é do cadastro, não da oportunidade, então vale para todos os cards do
   * mesmo cliente.
   */
  private statusPorCliente: Record<number, StatusClienteChave> = {};
  /** Usuários ativos: criado por, ações, comentários e anexos da ficha. */
  peopleList: Person[] = [];

  // ----- Escopo e vendedores (GET Oportunidade/configuracoes) -----
  /** Escopo de visualização do usuário logado. Sem ele o board não carrega (CA-13). */
  escopo: VendaEscopoStruct | null = null;
  /** Vendedores que o usuário enxerga; vazia = sem restrição. */
  idsVendedoresVisiveis: number[] = [];
  /** Todos os vendedores da empresa, inclusive inativos (o card pode estar com um deles). */
  vendedores: VendedorLookupStruct[] = [];
  private isRestritoVendedor = false;
  private vendedoresPorId = new Map<number, VendedorLookupStruct>();
  /** Opções do filtro "Vendedor responsável": ativos dentro do escopo. */
  vendedoresFiltro: VendedorLookupStruct[] = [];
  /** Falha ao resolver o escopo: nenhuma oportunidade é carregada. */
  private escopoIndisponivel = false;

  cardsByStage: Record<string, OportunidadeCard[]> = {};
  listaCards: OportunidadeCard[] = [];

  /** Paginação por coluna do Kanban, espelhando o processo-compras. */
  etapaPages: Record<string, EtapaPageState> = {};
  readonly kanbanPageSize = 20;

  readonly listPageSize = 50;
  listaPaginaAtual = 0;
  listaTotalRegistros = 0;
  listaUltimaPagina = true;
  listaCarregando = false;
  private listaToken = 0;
  private kanbanToken = 0;
  /** Filtros com que o Kanban foi carregado: ao voltar da Lista, recarrega se a visão ou os filtros mudaram. */
  private filtrosDoKanban = '';
  draggedCard: OportunidadeCard | null = null;
  /** Oportunidades com a troca de favorito em andamento: a estrela fica desabilitada até a resposta. */
  private favoritandoIds = new Set<number>();
  /** Oportunidade cuja ficha está sendo buscada: o card mostra o spinner e novos cliques esperam. */
  idOportunidadeAbrindo: number | null = null;
  suppressCardMoveAnimation = false;

  // ----- Catálogos -----
  /** Catálogo real de status_crm (db_pessoa). Chega junto dos demais lookups. */
  statusCliente: StatusClienteInfo[] = [];
  /** O status é do cadastro do cliente: trocar exige Alterar do CRM e Alterar Pessoas. */
  motivoStatusClienteSemPermissao: string | null = null;
  readonly prioridades = PRIORIDADES;
  readonly visaoOpcoes = VISAO_OPCOES;
  readonly periodoOpcoes = PERIODO_OPCOES;
  readonly campoDataOpcoes = CAMPO_DATA_OPCOES;
  readonly faixaValorOpcoes = FAIXA_VALOR_OPCOES;
  /** Catálogo real de tag_crm, com cor — alimenta o painel de tags do card. */
  tagsDisponiveis: TagOportunidade[] = [];
  /** origem_lead — o filtro manda o id para o servidor. */
  origens: LookupOportunidade[] = [];
  /** Opções de Resultado e Motivo da etapa Encerrado — o diálogo de encerramento trabalha com o id da opção. */
  private lookupResultados: CrmCampoDefinicaoOpcaoDto[] = [];
  private lookupMotivos: CrmCampoDefinicaoOpcaoDto[] = [];
  /** canal_contato — usado só na criação. */
  private lookupCanais: LookupOportunidade[] = [];


  /** Nomes e rótulos que o card exibe mas que vêm de outros bancos / lookups. */
  private contexto: ContextoMapeamento = {
    pessoasPorId: new Map<number, string>(),
    contatosPorId: new Map<number, ContatoPessoaStruct>(),
    vendedoresPorId: new Map<number, VendedorLookupStruct>(),
    origensPorId: new Map<number, string>(),
  };

  // ----- Filtros -----
  /** Pills dedicados: ficam fora do formGroup porque não são resetados por "Limpar Filtros" (CA05). */
  visao: VisaoBoard = 'ativas';
  periodo: PeriodoBoard = PERIODO_PADRAO;
  intervaloDe: Date | null = null;
  /** "do intervalo" clicado no menu de período: só então os campos De e Até aparecem. */
  intervaloAberto = false;
  intervaloAte: Date | null = null;

  filterModel = this.fb.group({
    searchText: [''],
    filterRespId: [null as number | null],
    // Responsável pela próxima ação: usuário (não vendedor), da mesma lista que a ficha usa.
    filterRespAcaoId: [null as number | null],
    filterOrigem: [null as number | null],
    filterFaixaValor: [''],
    filterPrioridade: [''],
    filterStage: [''],
    // Sobre qual data o pill de Período incide. Pré-selecionado em "criação" — mesmo
    // recorte de antes deste campo existir (PENDENCIAS.md §1.2/3.2).
    filterCampoData: [CAMPO_DATA_PADRAO as CampoDataFiltro],
  });

  private appliedFilters = {
    searchText: '',
    filterRespId: null as number | null,
    filterRespAcaoId: null as number | null,
    filterOrigem: null as number | null,
    filterFaixaValor: '',
    filterPrioridade: '',
    filterStage: '',
    filterCampoData: CAMPO_DATA_PADRAO as CampoDataFiltro,
    /**
     * Cliente vindo do "Ver todos no CRM" do cadastro de Pessoa. Fica fora do filterModel: não
     * tem campo no menu de filtros e não entra no filtro salvo.
     */
    idPessoaCliente: null as number | null,
  };

  /**
   * Ação pedida pela URL (cadastro de Pessoa / dialog de oportunidades do cliente). Executada só
   * depois que etapas e catálogos chegam — a ficha e a Nova oportunidade dependem deles.
   */
  private acaoUrlPendente: {
    idOportunidade: number | null;
    novaOportunidadeIdPessoaCliente: number | null;
    origemNovaOportunidade: OrigemNovaOportunidade | null;
  } | null = null;

  // ----- Lista -----
  readonly listDisplayedColumns: string[] = [
    'cliente', 'assunto', 'etapa', 'responsavel', 'proximaAcao', 'quando', 'tags', 'valor', 'actions',
  ];
  listSortActive = 'cliente';
  listSortDirection: SortDirection = 'asc';

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private crmEtapasKanbanService: CrmEtapasKanbanService,
    private oportunidadeService: OportunidadeService,
    private tagCrmService: TagCrmService,
    private pessoaLookupService: PessoaLookupService,
    private pessoaService: PessoaService,
    private authService: AuthService,
    private envolvidosEtapaService: EnvolvidosEtapaService,
    private usuarioService: UsuarioService,
    private alertService: AlertService,
    private utilService: UtilService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    const listaPermissoes = this.authService.getUserInfos()?.listaPermissoes;
    this.motivoStatusClienteSemPermissao = !listaPermissoes?.includes(PermissaoEnum.Vendas_Crm_Alterar)
      ? 'Sem permissão para alterar o CRM.'
      : !listaPermissoes.includes(PermissaoEnum.Cadastros_Pessoas_Alterar)
        ? 'Sem permissão para alterar o cadastro de pessoas.'
        : null;

    this.carregarFiltrosSalvos();
    this.lerParametrosUrl();
    this.assinarBuscaDebounced();
    this.carregarEtapas();
    this.carregarMovimentacoes();
    this.carregarCatalogos();
  }

  ngAfterViewInit(): void {
    // setTimeout e não direto no hook: escrever em indicadorX/W durante o AfterViewInit
    // dispararia "changed after it was checked" em modo dev.
    setTimeout(() => this.posicionarIndicadorAbas());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.filterModel, ['/vendas/crm']);
  }

  // ======================================================================
  // Carga
  // ======================================================================
  private carregarFiltrosSalvos(): void {
    const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
    if (!filtroSalvo) return;

    this.filterModel.patchValue(filtroSalvo, { emitEvent: false });
    this.snapshotFiltros();
  }

  /**
   * Parâmetros de entrada vindos de outras telas. O filtro de cliente entra antes da primeira
   * busca (o board já nasce filtrado); abrir a ficha ou a Nova oportunidade espera etapas e
   * catálogos. A URL é limpa depois, para o F5 não repetir a ação.
   */
  private lerParametrosUrl(): void {
    const params = this.route.snapshot.queryParamMap;
    const nomes = ['idOportunidade', 'idPessoaCliente', 'novaOportunidadeIdPessoaCliente'];
    if (!nomes.some(nome => params.has(nome))) return;

    const lerId = (nome: string): number | null => {
      const valor = Number(params.get(nome));
      return Number.isInteger(valor) && valor > 0 ? valor : null;
    };

    const idPessoaCliente = lerId('idPessoaCliente');
    if (idPessoaCliente) {
      this.appliedFilters = { ...this.appliedFilters, idPessoaCliente };
      // O nome do chip não pode depender de haver card desse cliente no resultado.
      this.pessoaLookupService.getListPessoaIds({ listIdPessoa: [idPessoaCliente] }).subscribe({
        next: response => {
          if (!response.isError) this.registrarPessoas(response.listaPessoa ?? []);
        },
        error: () => { /* o chip fica com o código no lugar do nome */ },
      });
    }

    this.acaoUrlPendente = {
      idOportunidade: lerId('idOportunidade'),
      novaOportunidadeIdPessoaCliente: lerId('novaOportunidadeIdPessoaCliente'),
      // Valor desconhecido cai no texto genérico da Nova oportunidade.
      origemNovaOportunidade: params.get('origemNovaOportunidade') as OrigemNovaOportunidade | null,
    };
  }

  /** Chamado por quem terminar por último entre etapas e catálogos. */
  private executarAcaoUrl(): void {
    if (!this.acaoUrlPendente || this.etapasCarregando || !this.contextoPronto) return;

    const { idOportunidade, novaOportunidadeIdPessoaCliente, origemNovaOportunidade } = this.acaoUrlPendente;
    this.acaoUrlPendente = null;

    if (idOportunidade) {
      this.abrirFichaPorId(idOportunidade, 'Oportunidade não encontrada.', 'Não foi possível abrir a oportunidade.');
    } else if (novaOportunidadeIdPessoaCliente) {
      this.novaOportunidadeDaPessoa(novaOportunidadeIdPessoaCliente, origemNovaOportunidade);
    }

    this.limparParametrosUrl();
  }

  private limparParametrosUrl(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        idOportunidade: null,
        idPessoaCliente: null,
        novaOportunidadeIdPessoaCliente: null,
        origemNovaOportunidade: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private assinarBuscaDebounced(): void {
    this.filterModel.controls.searchText.valueChanges
      .pipe(
        debounceTime(350),
        map(valor => (valor ?? '').trim()),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(termo => {
        if (termo === this.appliedFilters.searchText) return;
        this.appliedFilters = { ...this.appliedFilters, searchText: termo };
        this.recarregar();
      });
  }

  private carregarEtapas(): void {
    this.etapasCarregando = true;

    this.crmEtapasKanbanService.getAll('').subscribe({
      next: response => {
        this.etapasCarregando = false;

        if (response.isError) {
          this.alertService.show(
            'Atenção',
            response.errorDescription || 'Não foi possível carregar as etapas do kanban.',
            AlertType.warning
          );
          this.aplicarEtapas([]);
          return;
        }

        this.aplicarEtapas(response.listaEtapas ?? []);
      },
      error: () => {
        this.etapasCarregando = false;
        this.alertService.show('Erro', 'Não foi possível carregar as etapas do kanban.', AlertType.error);
        this.aplicarEtapas([]);
      },
    });
  }

  private carregarMovimentacoes(): void {
    this.crmEtapasKanbanService.listarMovimentacoes().subscribe({
      next: res => {
        if (res.isError) return;

        const mapa: Record<string, string[]> = {};
        for (const m of res.lista ?? []) {
          (mapa[m.origemChave] ??= []).push(m.destinoChave);
        }
        this.transicoes = mapa;
      },
    });
  }

  /**
   * Usuários, pessoas, tags, lookups e o escopo com os vendedores chegam de APIs diferentes e
   * o card depende deles para exibir nome em vez de id. Carrega tudo junto e só então busca os cards.
   */
  private carregarCatalogos(): void {
    forkJoin({
      usuarios: this.usuarioService.getAll(),
      // Página 0: a paginação da API é base zero. Pedir a 1 devolvia a segunda página, e o
      // painel de tags do card aparecia vazio em catálogo pequeno.
      tags: this.tagCrmService.getAll('', 0, 500),
      lookups: this.oportunidadeService.getLookups(),
      lookupPessoa: this.pessoaLookupService.getLookup(),
      // Falha aqui tem mensagem própria e bloqueia só as oportunidades (CA-13); os demais catálogos continuam aplicados.
      configuracoes: this.oportunidadeService.getConfiguracoes().pipe(catchError(() => of(null))),
    }).subscribe({
      next: ({ usuarios, tags, lookups, lookupPessoa, configuracoes }) => {
        this.mapearUsuarios(usuarios.listaUsuario ?? []);

        // Os catálogos entram antes do teste do escopo: a falha das configurações bloqueia só as oportunidades.
        this.statusCliente = (lookupPessoa.listaStatusCrm ?? []).map(item => ({
          chave: item.id,
          label: item.descricao,
          cor: corDoStatusCrm(item.descricao),
        }));

        this.tagsDisponiveis = (tags.listaTags ?? []).map(t => ({
          idTagCrm: t.idTagCrm,
          descricao: t.descricao,
          cor: t.cor,
        }));

        this.contexto.origensPorId = mapaDeLookup(lookups.origensLead ?? []);
        this.origens = lookups.origensLead ?? [];
        this.lookupResultados = lookups.resultados ?? [];
        this.lookupMotivos = lookups.motivosEncerramento ?? [];
        this.lookupCanais = lookups.canaisContato ?? [];

        if (!configuracoes || configuracoes.isError || !configuracoes.escopo) {
          // VIS-07: sem escopo resolvido nada é exibido — nunca libera por falha.
          this.escopoIndisponivel = true;
          this.oportunidadesCarregando = false;
          this.alertService.show(
            'Aviso',
            configuracoes?.errorDescription
              || 'Não foi possível verificar o seu escopo de visualização. Tente novamente em alguns instantes.',
            AlertType.warning
          );
          if (this.acaoUrlPendente) {
            this.acaoUrlPendente = null;
            this.limparParametrosUrl();
          }
          return;
        }

        this.aplicarConfiguracoes(configuracoes.escopo, configuracoes.idsVendedoresVisiveis ?? [],
          configuracoes.vendedores ?? [], configuracoes.isRestritoVendedor);

        // Etapas e catálogos chegam em respostas separadas; quem terminar por último busca.
        this.contextoPronto = true;
        if (this.allStages.length) this.recarregar();

        this.executarAcaoUrl();
      },
      error: () => {
        this.mapearUsuarios([]);
        this.oportunidadesCarregando = false;
        this.alertService.show('Erro', 'Não foi possível carregar os cadastros de apoio do CRM.', AlertType.error);

        // Sem catálogos a ação da URL não roda; a URL é limpa do mesmo jeito.
        if (this.acaoUrlPendente) {
          this.acaoUrlPendente = null;
          this.limparParametrosUrl();
        }
      },
    });
  }

  private mapearUsuarios(usuarios: UsuarioListaStruct[]): void {
    this.peopleList = usuarios
      .filter(u => u.ativo && !u.deletado)
      .map((u, i) => ({
        idUsuario: u.idUsuario,
        name: u.nomeUsuario,
        initials: iniciaisDoNome(u.nomeUsuario),
        color: AVATAR_COLORS[i % AVATAR_COLORS.length],
      }))
      .sort((a, b) => compararValoresOrdenacao(a.name, b.name));
  }

  /**
   * Vendedores pelo id para o card, a lista e a ficha; o filtro oferece só os ativos dentro do
   * escopo (idsVendedoresVisiveis vazia = sem restrição).
   */
  private aplicarConfiguracoes(escopo: VendaEscopoStruct, idsVendedoresVisiveis: number[],
    vendedores: VendedorLookupStruct[], isRestritoVendedor: boolean): void {
    this.escopo = escopo;
    this.idsVendedoresVisiveis = idsVendedoresVisiveis;
    this.vendedores = vendedores;
    this.isRestritoVendedor = isRestritoVendedor;
    this.vendedoresPorId = new Map(vendedores.map(v => [v.idVendedor, v]));
    this.contexto.vendedoresPorId = this.vendedoresPorId;

    this.vendedoresFiltro = vendedores.filter(v => v.idSituacaoVendedor !== SituacaoVendedorEnum.Inativo
      && (!idsVendedoresVisiveis.length || idsVendedoresVisiveis.includes(v.idVendedor)));

    this.sanearVendedorFiltrado();
  }

  /** O filtro salvo pode ter um vendedor que saiu do escopo ou ficou inativo (ou um id de usuário antigo). */
  private sanearVendedorFiltrado(): void {
    const idVendedor = this.appliedFilters.filterRespId;
    if (idVendedor == null || this.vendedoresFiltro.some(v => v.idVendedor === idVendedor)) return;

    this.filterModel.patchValue({ filterRespId: null }, { emitEvent: false });
    this.appliedFilters = { ...this.appliedFilters, filterRespId: null };
  }

  /**
   * Faixa acima do board. Só aparece em escopo restritivo — quem vê tudo não precisa ser
   * lembrado disso a cada carga.
   */
  get textoEscopo(): string | null {
    if (!this.escopo || this.escopo.isSemRestricao) return null;

    if (this.escopo.idEscopoVendedor === EscopoVisualizacaoEnum.Equipe) {
      const qtd = this.escopo.qtdSubordinados;
      return `Escopo de visualização: Ver a equipe (${qtd} ${qtd === 1 ? 'subordinado' : 'subordinados'})`;
    }

    return 'Escopo de visualização: Ver apenas os próprios';
  }

  /**
   * Monta o filtro que vai para o servidor. Com paginação, filtrar em memória filtraria só
   * a página carregada — todo recorte precisa ser do servidor.
   */
  private montarFiltros(chaveEtapa?: string): GetOportunidadesFiltroRequest {
    const { searchText, filterRespId, filterRespAcaoId, filterOrigem, filterFaixaValor, filterPrioridade,
      filterStage, filterCampoData, idPessoaCliente } = this.appliedFilters;

    const faixa = this.faixaDeValor(filterFaixaValor);
    const periodo = this.intervaloDoPeriodo();

    return {
      pesquisa: searchText || undefined,
      chaveEtapa: chaveEtapa ?? (filterStage || undefined),
      idVendedorResponsavel: filterRespId ?? undefined,
      idUsuarioResponsavelAcao: filterRespAcaoId ?? undefined,
      idPessoaCliente: idPessoaCliente ?? undefined,
      idTipoPrioridade: filterPrioridade
        ? ID_POR_PRIORIDADE[filterPrioridade as PrioridadeChave]
        : undefined,
      idOrigemLead: filterOrigem ?? undefined,
      valorMinimo: faixa.minimo,
      valorMaximo: faixa.maximo,
      // Encerradas e Favoritas também são só das não arquivadas.
      isArquivado: this.visao === 'arquivadas',
      comAcaoPendente: this.visao === 'pendentes' ? true : undefined,
      isEncerradas: this.visao === 'encerradas' ? true : undefined,
      isFavoritas: this.visao === 'favoritas' ? true : undefined,
      dataInicio: periodo.inicio,
      dataFim: periodo.fim,
      filtroData: ID_POR_CAMPO_DATA[filterCampoData],
    };
  }

  private faixaDeValor(faixa: string): { minimo?: number; maximo?: number } {
    switch (faixa) {
      case 'lt10': return { maximo: 10000 };
      case '10a50': return { minimo: 10000, maximo: 50000 };
      case 'gt50': return { minimo: 50000 };
      default: return {};
    }
  }

  /**
   * Traduz o pill de período em intervalo ISO. Sobre qual data esse intervalo incide é
   * decidido pelo filtro avançado "Filtrar período por" (filterCampoData, padrão criação) —
   * ver PENDENCIAS.md §1.2/3.2.
   */
  private intervaloDoPeriodo(): { inicio?: string; fim?: string } {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    const iso = (data: Date) => data.toISOString();
    const fimDoDia = (data: Date) => {
      const copia = new Date(data);
      copia.setHours(23, 59, 59, 999);
      return copia;
    };

    switch (this.periodo) {
      case 'hoje':
        return { inicio: iso(hoje), fim: iso(fimDoDia(hoje)) };

      case 'semana': {
        const inicio = new Date(hoje);
        inicio.setDate(hoje.getDate() - hoje.getDay());
        const fim = new Date(inicio);
        fim.setDate(inicio.getDate() + 6);
        return { inicio: iso(inicio), fim: iso(fimDoDia(fim)) };
      }

      case 'mes': {
        const inicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
        const fim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
        return { inicio: iso(inicio), fim: iso(fimDoDia(fim)) };
      }

      case '30d': {
        const inicio = new Date(hoje);
        inicio.setDate(hoje.getDate() - 30);
        return { inicio: iso(inicio), fim: iso(fimDoDia(hoje)) };
      }

      case 'intervalo': {
        if (!this.intervaloDe || !this.intervaloAte) return {};
        const de = new Date(this.intervaloDe);
        de.setHours(0, 0, 0, 0);
        return { inicio: iso(de), fim: iso(fimDoDia(new Date(this.intervaloAte))) };
      }

      default:
        return {};
    }
  }

  /** Recarrega só a visão aberta — a outra recarrega ao trocar de aba. */
  private recarregar(): void {
    if (this.activeTab === 'kanban') {
      this.carregarKanban();
    } else {
      // Filtro novo ou card alterado na Lista: o Kanban carregado deixa de valer e recarrega ao voltar.
      this.filtrosDoKanban = '';
      this.carregarLista();
    }
  }

  // ----- Kanban -----

  private carregarKanban(): void {
    if (!this.allStages.length || this.escopoIndisponivel) return;

    const token = ++this.kanbanToken;
    this.oportunidadesCarregando = true;
    const filtros = this.montarFiltros();

    this.oportunidadeService.getKanban(filtros, this.kanbanPageSize).subscribe({
      next: response => {
        if (token !== this.kanbanToken) return;
        this.oportunidadesCarregando = false;

        if (response.isError) {
          this.alertService.show(
            'Atenção',
            response.errorDescription || 'Não foi possível carregar as oportunidades.',
            AlertType.warning
          );
          return;
        }

        const etapas = response.etapas ?? [];
        const itens = etapas.flatMap(etapa => etapa.cards ?? []);
        this.comPessoasResolvidas(itens, () => {
          // A resolução das pessoas é assíncrona: uma carga mais nova pode ter começado nesse meio-tempo.
          if (token !== this.kanbanToken) return;
          this.aplicarKanbanPaginado(etapas);
          this.colapsarEtapasSemAcesso();
          this.filtrosDoKanban = JSON.stringify(filtros);
        });
      },
      error: () => {
        if (token !== this.kanbanToken) return;
        this.oportunidadesCarregando = false;
        this.alertService.show('Erro', 'Não foi possível carregar as oportunidades.', AlertType.error);
      },
    });
  }

  private aplicarKanbanPaginado(etapas: OportunidadeKanbanEtapaItemComValorStruct[]): void {
    const cards: Record<string, OportunidadeCard[]> = {};
    const estados: Record<string, EtapaPageState> = {};

    // Toda etapa configurada precisa de entrada, mesmo sem card: a coluna existe no board.
    this.allStages.forEach(stage => {
      cards[stage.key] = [];
      estados[stage.key] = { ...this.novoEstadoEtapa(), carregadaAlgumaVez: true };
    });

    for (const grupo of etapas) {
      if (!grupo.chaveEtapa || !estados[grupo.chaveEtapa]) continue;
      cards[grupo.chaveEtapa] = (grupo.cards ?? []).map(item => mapearCard(item, this.contexto));
      estados[grupo.chaveEtapa] = {
        paginaAtual: 0,
        carregando: false,
        temMais: grupo.temMais,
        totalRegistros: grupo.totalRegistros,
        somaValor: grupo.somaValor,
        carregadaAlgumaVez: true,
        token: 0,
      };
    }

    this.cardsByStage = cards;
    this.etapaPages = estados;
  }

  private novoEstadoEtapa(): EtapaPageState {
    return {
      paginaAtual: 0, carregando: false, temMais: false,
      totalRegistros: 0, somaValor: 0, carregadaAlgumaVez: false, token: 0,
    };
  }

  etapaEstado(stageKey: string): EtapaPageState {
    return this.etapaPages[stageKey] ?? ESTADO_ETAPA_VAZIO;
  }

  carregarMaisDaEtapa(stageKey: string): void {
    const estado = this.etapaPages[stageKey];
    if (!estado || estado.carregando || !estado.temMais) return;
    this.buscarPaginaEtapa(stageKey, estado.paginaAtual + 1, false);
  }

  /** Recarrega uma coluna sozinha — usado depois de mover card, sem repuxar o board todo. */
  private recarregarEtapa(stageKey: string): void {
    if (!this.etapaPages[stageKey]) return;
    this.buscarPaginaEtapa(stageKey, 0, true);
  }

  private buscarPaginaEtapa(chaveEtapa: string, pagina: number, substituir: boolean): void {
    const estado = this.etapaPages[chaveEtapa];
    if (!estado) return;

    // Token por etapa: resposta atrasada de uma busca antiga não pode sobrescrever a nova.
    const token = ++estado.token;
    estado.carregando = true;

    this.oportunidadeService
      .getKanbanEtapa(this.montarFiltros(chaveEtapa), pagina, this.kanbanPageSize)
      .subscribe({
        next: response => {
          if (token !== estado.token || this.etapaPages[chaveEtapa] !== estado) return;
          estado.carregando = false;
          if (response.isError) return;

          const itens = response.cards ?? [];
          this.comPessoasResolvidas(itens, () => {
            const cards = itens.map(item => mapearCard(item, this.contexto));
            const trocar = substituir || !estado.carregadaAlgumaVez;
            this.cardsByStage[chaveEtapa] = trocar
              ? cards
              : [...(this.cardsByStage[chaveEtapa] ?? []), ...cards];
            this.cardsByStage = { ...this.cardsByStage };

            estado.paginaAtual = pagina;
            estado.temMais = response.temMais;
            estado.totalRegistros = response.totalRegistros;
            estado.somaValor = response.somaValor;
            estado.carregadaAlgumaVez = true;
          });
        },
        error: () => {
          if (token !== estado.token || this.etapaPages[chaveEtapa] !== estado) return;
          estado.carregando = false;
        },
      });
  }

  // ----- Lista -----

  private carregarLista(): void {
    this.buscarPaginaLista(0, true);
  }

  carregarMaisCards(): void {
    if (this.listaCarregando || this.listaUltimaPagina) return;
    this.buscarPaginaLista(this.listaPaginaAtual + 1, false);
  }

  private buscarPaginaLista(pagina: number, substituir: boolean): void {
    if (this.escopoIndisponivel) return;
    const token = ++this.listaToken;
    this.listaCarregando = true;

    this.oportunidadeService.getLista(this.montarFiltros(), pagina, this.listPageSize).subscribe({
      next: response => {
        if (token !== this.listaToken) return;
        this.listaCarregando = false;

        if (response.isError) {
          this.listaUltimaPagina = true;
          this.alertService.show(
            'Atenção',
            response.errorDescription || 'Não foi possível carregar as oportunidades.',
            AlertType.warning
          );
          return;
        }

        const itens = response.itens ?? [];
        this.comPessoasResolvidas(itens, () => {
          const cards = itens.map(item => mapearCard(item, this.contexto));
          this.listaCards = substituir ? cards : [...this.listaCards, ...cards];
          this.listaPaginaAtual = response.pagina ?? pagina;
          this.listaTotalRegistros = response.totalRegistros;
          this.listaUltimaPagina = !response.temMais;
          this.ordenarLista();
        });
      },
      error: () => {
        if (token !== this.listaToken) return;
        this.listaCarregando = false;
        this.listaUltimaPagina = true;
        this.alertService.show('Erro', 'Não foi possível carregar as oportunidades.', AlertType.error);
      },
    });
  }

  /**
   * Nome e status do cliente vivem em db_pessoa. Resolve só os ids que aparecem nos cards
   * carregados e que ainda não estão em cache.
   *
   * NÃO usar listPessoaSelect para isso: ele pagina em 20 por padrão, então cliente fora
   * das 20 primeiras pessoas da empresa apareceria como "Cliente 123" no card.
   */
  private comPessoasResolvidas(itens: OportunidadeKanbanItem[], aplicar: () => void): void {
    const clientes = new Set<number>();
    const contatos = new Set<number>();

    for (const item of itens) {
      if (!this.contexto.pessoasPorId.has(item.idPessoaCliente)) {
        clientes.add(item.idPessoaCliente);
      }
      if (item.idPessoaContato && !this.contexto.contatosPorId.has(item.idPessoaContato)) {
        contatos.add(item.idPessoaContato);
      }
    }

    if (!clientes.size && !contatos.size) {
      aplicar();
      return;
    }

    // Cliente vive em pessoa e contato em pessoa_contato: dois lookups distintos. Card sem
    // nome ainda é melhor que board vazio, então falha em qualquer um dos dois não bloqueia.
    forkJoin({
      pessoas: clientes.size
        ? this.pessoaLookupService.getListPessoaIds({ listIdPessoa: [...clientes] })
            .pipe(catchError(() => of(null)))
        : of(null),
      contatos: contatos.size
        ? this.pessoaLookupService.getListContatoIds([...contatos]).pipe(catchError(() => of(null)))
        : of(null),
    }).subscribe({
      next: ({ pessoas, contatos: respostaContatos }) => {
        if (pessoas) this.registrarPessoas(pessoas.listaPessoa ?? []);

        for (const contato of respostaContatos?.list ?? []) {
          this.contexto.contatosPorId.set(contato.id, contato);
        }

        aplicar();
      },
      error: () => aplicar(),
    });
  }

  private registrarPessoas(pessoas: Pessoa[]): void {
    const status = { ...this.statusPorCliente };

    for (const pessoa of pessoas) {
      this.contexto.pessoasPorId.set(pessoa.idPessoa, pessoa.nome);
      if (pessoa.idStatusCrm != null) status[pessoa.idPessoa] = pessoa.idStatusCrm;
    }

    this.statusPorCliente = status;
  }

  /**
   * Tira o card das duas visões sem ir ao servidor. O total da coluna também cai: ele veio
   * de uma contagem do servidor e ficaria um a mais até a próxima carga.
   */
  private removerCardDaTela(card: OportunidadeCard): void {
    const estado = this.etapaPages[card.stage];
    if (estado && estado.totalRegistros > 0) {
      estado.totalRegistros -= 1;
      estado.somaValor -= card.valor || 0;
    }

    this.cardsByStage[card.stage] = (this.cardsByStage[card.stage] ?? [])
      .filter(c => c.idOportunidade !== card.idOportunidade);
    this.cardsByStage = { ...this.cardsByStage };

    this.listaCards = this.listaCards.filter(c => c.idOportunidade !== card.idOportunidade);
    if (this.listaTotalRegistros > 0) this.listaTotalRegistros -= 1;
  }

  private aplicarEtapas(etapas: CrmEtapaOportunidade[]): void {
    // Etapa inativa não aparece no board operacional (CA02); a ordem é a do fluxo configurado (CA01).
    const normalizadas = etapas
      .filter(etapa => etapa.isAtivo)
      .map(etapa => this.mapEtapa(etapa))
      .sort((a, b) => a.ordem - b.ordem || compararValoresOrdenacao(a.name, b.name));

    // Agrupa por isFinal, igual à tela de configuração: as etapas finais ficam depois do
    // divisor. isEspecial continua governando só a regra de alcance em canDrop().
    this.mainStages = normalizadas.filter(e => !e.isFinal);
    this.sideStages = normalizadas.filter(e => e.isFinal);
    this.allStages = [...this.mainStages, ...this.sideStages];

    this.sanearEtapaFiltrada();
    // As colunas só existem depois das etapas: é aqui que a primeira carga acontece.
    if (this.contextoPronto) this.recarregar();
    this.executarAcaoUrl();
  }

  private mapEtapa(etapa: CrmEtapaOportunidade): OportunidadeStage {
    const color = etapa.cor || '#1E5DB8';
    return {
      key: etapa.chave,
      idEtapaKanban: etapa.idEtapaKanban,
      name: etapa.nome,
      descricao: etapa.descricao ?? '',
      icon: etapa.icone || 'description',
      color,
      tint: etapa.tint || `${color}22`,
      ordem: etapa.ordem,
      isEspecial: etapa.isEspecial,
      isFinal: etapa.isFinal,
      idEtapaKanbanRetornoReabertura: etapa.idEtapaKanbanRetornoReabertura,
      podeVisualizar: etapa.podeVisualizar,
    };
  }

  /** A etapa filtrada pode ter sido inativada ou excluída na configuração. */
  private sanearEtapaFiltrada(): void {
    const chave = this.appliedFilters.filterStage;
    if (!chave || this.allStages.some(s => s.key === chave)) return;

    this.filterModel.patchValue({ filterStage: '' }, { emitEvent: false });
    this.appliedFilters = { ...this.appliedFilters, filterStage: '' };
  }

  // ======================================================================
  // Filtragem e distribuição pelas colunas
  // ======================================================================
  aplicarFiltros(): void {
    this.snapshotFiltros();
    this.recarregar();
  }

  /**
   * Limpa busca, filtros avançados e as pílulas de Visão e Período, que voltam a Ativas e todas
   * (pedido do teste da US 148709, no lugar do CA05, que mantinha a Visão). O campo de data
   * volta para "criação" — o padrão, não vazio: ele sempre tem um valor selecionado.
   */
  limparFiltros(): void {
    this.filterModel.patchValue(
      {
        searchText: '',
        filterRespId: null,
        filterRespAcaoId: null,
        filterOrigem: null,
        filterFaixaValor: '',
        filterPrioridade: '',
        filterStage: '',
        filterCampoData: CAMPO_DATA_PADRAO,
      },
      { emitEvent: false }
    );
    this.appliedFilters = { ...this.appliedFilters, idPessoaCliente: null };
    // As pílulas de visão e período também são filtros: voltam ao padrão (Ativas, todas).
    this.visao = 'ativas';
    this.periodo = PERIODO_PADRAO;
    this.intervaloDe = null;
    this.intervaloAte = null;
    this.aplicarFiltros();
  }

  private snapshotFiltros(): void {
    const { searchText, filterRespId, filterRespAcaoId, filterOrigem, filterFaixaValor, filterPrioridade,
      filterStage, filterCampoData } = this.filterModel.value;
    this.appliedFilters = {
      searchText: (searchText ?? '').trim(),
      filterRespId: filterRespId ?? null,
      filterRespAcaoId: filterRespAcaoId ?? null,
      filterOrigem: filterOrigem ?? null,
      filterFaixaValor: filterFaixaValor ?? '',
      filterPrioridade: filterPrioridade ?? '',
      filterStage: filterStage ?? '',
      filterCampoData: (filterCampoData as CampoDataFiltro) ?? CAMPO_DATA_PADRAO,
      // Não tem campo no filterModel: só sai pelo chip ou por "Limpar Filtros".
      idPessoaCliente: this.appliedFilters.idPessoaCliente,
    };
  }

  get qtdFiltrosAvancados(): number {
    const { filterRespId, filterRespAcaoId, filterOrigem, filterFaixaValor, filterPrioridade, filterStage,
      filterCampoData, idPessoaCliente } = this.appliedFilters;
    const contaEtapa = this.activeTab === 'lista' && !!filterStage ? 1 : 0;
    // filterCampoData sempre tem valor (não há "vazio" nele) — só conta como filtro ativo
    // quando difere do padrão, senão o badge do botão Filtrar ficaria sempre aceso.
    const contaCampoData = filterCampoData !== CAMPO_DATA_PADRAO ? 1 : 0;
    return (
      (filterRespId != null ? 1 : 0) +
      (filterRespAcaoId != null ? 1 : 0) +
      (filterOrigem ? 1 : 0) +
      (filterFaixaValor ? 1 : 0) +
      (filterPrioridade ? 1 : 0) +
      contaEtapa +
      contaCampoData +
      (idPessoaCliente != null ? 1 : 0)
    );
  }

  get hasActiveFilters(): boolean {
    const { searchText, filterRespId, filterRespAcaoId, filterOrigem, filterFaixaValor, filterPrioridade,
      idPessoaCliente } = this.appliedFilters;
    return !!(searchText || filterRespId != null || filterRespAcaoId != null || filterOrigem || filterFaixaValor
      || filterPrioridade || idPessoaCliente != null);
  }

  get idPessoaClienteFiltro(): number | null {
    return this.appliedFilters.idPessoaCliente;
  }

  /** Até o lookup responder (ou se ele falhar), o chip mostra o código da pessoa. */
  get nomeClienteFiltro(): string {
    const id = this.appliedFilters.idPessoaCliente;
    if (id == null) return '';
    return this.contexto.pessoasPorId.get(id) ?? `#${id}`;
  }

  removerFiltroCliente(): void {
    this.appliedFilters = { ...this.appliedFilters, idPessoaCliente: null };
    this.recarregar();
  }

  // ----- Pills -----
  selecionarVisao(valor: VisaoBoard): void {
    if (this.visao === valor) return;
    this.visao = valor;
    // Cada visão é um conjunto diferente no servidor: precisa recarregar.
    this.recarregar();
  }

  get visaoLabel(): string {
    return this.visaoOpcoes.find(o => o.value === this.visao)?.label ?? 'Ativas';
  }

  /** CA04 — o substantivo do contador acompanha a visão, não só o número. */
  get contadorSubstantivo(): string {
    const termo = CONTADOR_SUBSTANTIVO[this.visao] ?? CONTADOR_SUBSTANTIVO.ativas;
    return this.totalCardsVisiveis === 1 ? termo.singular : termo.plural;
  }

  get periodoDestacado(): boolean {
    return this.periodo !== PERIODO_PADRAO;
  }

  selecionarPeriodo(valor: PeriodoBoard): void {
    this.periodo = valor;
    // "do intervalo" só aplica quando as duas datas estiverem preenchidas.
    if (valor !== 'intervalo') {
      this.intervaloDe = null;
      this.intervaloAte = null;
    }
    this.recarregar();
  }

  aplicarIntervalo(): void {
    if (!this.intervaloDe || !this.intervaloAte) return;
    this.periodo = 'intervalo';
    this.recarregar();
  }

  cancelarIntervalo(): void {
    if (this.periodo === 'intervalo') {
      this.periodo = PERIODO_PADRAO;
      this.intervaloDe = null;
      this.intervaloAte = null;
      this.recarregar();
    }
  }

  /** Em "do intervalo" o rótulo do pill passa a exibir De – Até (CA03). */
  get periodoLabel(): string {
    if (this.periodo === 'intervalo' && this.intervaloDe && this.intervaloAte) {
      return `${this.formatarData(this.intervaloDe)} – ${this.formatarData(this.intervaloAte)}`;
    }
    return this.periodoOpcoes.find(o => o.value === this.periodo)?.label ?? 'todas';
  }

  get visaoDestacada(): boolean {
    return this.visao !== 'ativas';
  }

  // ======================================================================
  // Board
  // ======================================================================
  getStage(key: string): OportunidadeStage {
    return this.allStages.find(s => s.key === key) || ETAPA_DESCONHECIDA;
  }

  colunaCards(stageKey: string): OportunidadeCard[] {
    return this.cardsByStage[stageKey] || [];
  }

  /** Total da coluna no servidor — não o que está carregado na tela. */
  totalDaEtapa(stageKey: string): number {
    return this.etapaEstado(stageKey).totalRegistros;
  }

  temMaisNaEtapa(stageKey: string): boolean {
    return this.etapaEstado(stageKey).temMais;
  }

  /**
   * Vale para a carga da coluna e para a do board inteiro: na primeira carga e ao trocar
   * filtro quem está carregando é o `getKanban`, não a paginação da etapa.
   */
  estaCarregandoEtapa(stageKey: string): boolean {
    return this.oportunidadesCarregando || this.etapaEstado(stageKey).carregando;
  }

  /** Total em R$ da coluna: soma do valor de todas as oportunidades visíveis dela, calculada pelo orquestrador. */
  totalValorDaEtapa(stageKey: string): string {
    const soma = this.etapaEstado(stageKey).somaValor;
    return this.formatarMoeda(soma);
  }

  /**
   * Contador do topo. No Kanban soma o total de cada coluna vindo do servidor, e não o que
   * está carregado — com paginação os dois divergem.
   */
  get totalCardsVisiveis(): number {
    if (this.activeTab === 'lista') return this.listaTotalRegistros;
    return this.allStages.reduce((acc, stage) => acc + this.totalDaEtapa(stage.key), 0);
  }

  isCollapsed(stageKey: string): boolean {
    return this.collapsedStages.has(stageKey);
  }

  /**
   * CA20 — etapa sem Visualizar para nenhum perfil do usuário começa recolhida, desde que
   * não tenha nenhum card dele. A segunda condição respeita o escape do repositório ("ou o
   * card é meu"): sem ela o vendedor perderia de vista os próprios cards numa etapa que o
   * perfil não enxerga.
   */
  private colapsarEtapasSemAcesso(): void {
    this.allStages.forEach(stage => {
      if (stage.podeVisualizar === false && this.totalDaEtapa(stage.key) === 0) {
        this.collapsedStages.add(stage.key);
      }
    });
  }

  /** A coluna recolhida por falta de permissão não abre no clique. */
  toggleCollapsedStage(stage: OportunidadeStage): void {
    if (stage.podeVisualizar === false) return;
    this.toggleCollapse(stage.key);
  }

  tooltipEtapaColapsada(stage: OportunidadeStage): string {
    return stage.podeVisualizar === false
      ? `Você não tem permissão para visualizar a etapa ${stage.name}.`
      : `Expandir ${stage.name}`;
  }

  toggleCollapse(stageKey: string): void {
    if (this.collapsedStages.has(stageKey)) this.collapsedStages.delete(stageKey);
    else this.collapsedStages.add(stageKey);
  }

  /**
   * Só destinos configurados na etapa de origem. A etapa especial (Encerrado) é alcançável
   * de qualquer etapa, e a etapa final não tem saída.
   */
  canDrop(fromStage: string, toStage: string): boolean {
    if (fromStage === toStage) return true;

    const origem = this.getStage(fromStage);
    if (origem.isFinal) return false;

    // Etapa especial/final é alcançável de qualquer etapa do fluxo (US §5, "Encerrado").
    const destino = this.getStage(toStage);
    if (destino.isEspecial || destino.isFinal) return true;

    return (this.transicoes[fromStage] || []).includes(toStage);
  }

  isDropBlocked(stageKey: string): boolean {
    if (!this.draggedCard) return false;
    return !this.canDrop(this.draggedCard.stage, stageKey);
  }

  isDraggable(card: OportunidadeCard): boolean {
    // Card em etapa final não é arrastável (CA16); com filtro ativo a ordem não reflete o board.
    return !this.getStage(card.stage).isFinal && !this.hasActiveFilters;
  }

  dragStarted(card: OportunidadeCard): void {
    this.draggedCard = card;
  }

  dragEnded(): void {
    this.draggedCard = null;
  }

  drop(event: CdkDragDrop<OportunidadeCard[]>, toStageKey: string): void {
    const card: OportunidadeCard = event.item.data;
    this.draggedCard = null;

    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      return;
    }

    const fromStageKey = card.stage;

    if (!this.canDrop(fromStageKey, toStageKey)) {
      this.alertService.show(
        'Atenção',
        `Não é permitido mover de "${this.getStage(fromStageKey).name}" para "${this.getStage(toStageKey).name}".`,
        AlertType.warning
      );
      return;
    }

    // Mover para a etapa final exige o desfecho antes de concluir a movimentação (CA15).
    if (this.getStage(toStageKey).isFinal) {
      this.encerrarOportunidade(card, toStageKey);
      return;
    }

    this.moverCard(card, toStageKey);
  }

  /**
   * Movimentação. A validação de verdade é do servidor (transição configurada, perfil e
   * campos obrigatórios do destino) — o canDrop local é só para não deixar arrastar à toa.
   * Em erro o card volta para a etapa de origem.
   */
  private moverCard(card: OportunidadeCard, toStageKey: string): void {
    const destino = this.getStage(toStageKey);
    const stageOrigem = card.stage;
    const idEtapaOrigem = card.idEtapaKanban;

    this.suppressProgrammaticCardAnimation();

    // Otimista: o card pula de coluna na hora. Se o servidor recusar, volta para a origem.
    this.moverCardEntreColunas(card, stageOrigem, destino);

    this.envolvidosEtapaService.getEnvolvidos(destino.idEtapaKanban)
      .pipe(
        switchMap(idsUsuarioNotificar => this.oportunidadeService.mover(card.idOportunidade, {
          idEtapaKanbanDestino: destino.idEtapaKanban,
          idsUsuarioNotificar,
        }))
      )
      .subscribe({
        next: response => {
          if (response.isError || !response.oportunidade) {
            this.desfazerMovimentacao(card, stageOrigem, idEtapaOrigem, destino.key);
            this.alertService.show(
              'Atenção',
              response.errorDescription || 'Não foi possível mover o card.',
              AlertType.warning
            );
            return;
          }

          // Só as duas colunas mudaram de total: repuxar o board inteiro seria desperdício.
          this.recarregarEtapa(stageOrigem);
          this.recarregarEtapa(destino.key);
          this.alertService.show('Sucesso', `Card movido para ${destino.name}.`, AlertType.success);
        },
        error: () => {
          this.desfazerMovimentacao(card, stageOrigem, idEtapaOrigem, destino.key);
          this.alertService.show('Erro', 'Não foi possível mover o card.', AlertType.error);
        },
      });
  }

  private moverCardEntreColunas(card: OportunidadeCard, deKey: string, destino: OportunidadeStage): void {
    this.cardsByStage[deKey] = (this.cardsByStage[deKey] ?? [])
      .filter(c => c.idOportunidade !== card.idOportunidade);
    this.transferirValorEntreEtapas(card, deKey, destino.key);

    this.posicionarCard(card, destino);

    const naDestino = this.cardsByStage[destino.key] ?? [];
    if (!naDestino.some(c => c.idOportunidade === card.idOportunidade)) {
      this.cardsByStage[destino.key] = [card, ...naDestino];
    }
    this.cardsByStage = { ...this.cardsByStage };
  }

  private desfazerMovimentacao(card: OportunidadeCard, stageOrigem: string,
    idEtapaOrigem: number, destinoKey: string): void {
    card.stage = stageOrigem;
    card.idEtapaKanban = idEtapaOrigem;

    this.cardsByStage[destinoKey] = (this.cardsByStage[destinoKey] ?? [])
      .filter(c => c.idOportunidade !== card.idOportunidade);
    this.transferirValorEntreEtapas(card, destinoKey, stageOrigem);

    const naOrigem = this.cardsByStage[stageOrigem] ?? [];
    if (!naOrigem.some(c => c.idOportunidade === card.idOportunidade)) {
      this.cardsByStage[stageOrigem] = [card, ...naOrigem];
    }
    this.cardsByStage = { ...this.cardsByStage };
  }

  /** Movimentação otimista: o total das duas colunas acompanha o card até a recarga delas. */
  private transferirValorEntreEtapas(card: OportunidadeCard, deKey: string, paraKey: string): void {
    const de = this.etapaPages[deKey];
    const para = this.etapaPages[paraKey];
    if (de) de.somaValor -= card.valor || 0;
    if (para) para.somaValor += card.valor || 0;
  }

  private posicionarCard(card: OportunidadeCard, destino: OportunidadeStage): void {
    const agora = Date.now();
    card.stage = destino.key;
    card.idEtapaKanban = destino.idEtapaKanban;
    card.updatedAt = agora;
    card.renderKey = `${card.id}-${destino.key}-${agora}`;
  }

  /**
   * Encerramento (CA15). Resultado e motivo são opções dos campos de sistema da etapa Encerrado;
   * o diálogo devolve o id e o rótulo de cada uma. Sem preenchimento a movimentação é abortada e
   * o card fica onde estava.
   */
  private encerrarOportunidade(card: OportunidadeCard, toStageKey: string): void {
    const destino = this.getStage(toStageKey);

    this.dialog
      .open(EncerrarOportunidadeDialogComponent, {
        data: {
          titulo: card.titulo,
          cliente: card.cliente,
          etapaNome: destino.name,
          resultados: this.lookupResultados,
          motivos: this.lookupMotivos,
        },
        autoFocus: false,
        maxWidth: '92vw',
      })
      .afterClosed()
      .subscribe((resultado?: EncerrarOportunidadeResult | null) => {
        if (!resultado) return;

        const idResultado = resultado.idCampoDefinicaoOpcaoResultado;
        const idMotivo = resultado.idCampoDefinicaoOpcaoMotivo;

        if (idResultado == null || idMotivo == null) {
          this.alertService.show(
            'Atenção',
            'Resultado ou motivo não reconhecido. Recarregue a tela e tente novamente.',
            AlertType.warning
          );
          return;
        }

        this.oportunidadeService
          .encerrar(card.idOportunidade, {
            idEtapaKanbanDestino: destino.idEtapaKanban,
            idCampoDefinicaoOpcaoResultado: idResultado,
            idCampoDefinicaoOpcaoMotivo: idMotivo,
            observacoesFinais: resultado.observacoes,
          })
          .subscribe({
            next: response => {
              if (response.isError || !response.oportunidade) {
                this.alertService.show(
                  'Atenção',
                  response.errorDescription || 'Não foi possível encerrar a oportunidade.',
                  AlertType.warning
                );
                return;
              }

              this.suppressProgrammaticCardAnimation();
              this.recarregar();
              this.alertService.show(
                'Oportunidade encerrada',
                `${card.titulo} — ${resultado.resultado} (${resultado.motivo}).`,
                AlertType.success
              );
            },
            error: () => {
              this.alertService.show('Erro', 'Não foi possível encerrar a oportunidade.', AlertType.error);
            },
          });
      });
  }

  private suppressProgrammaticCardAnimation(): void {
    this.suppressCardMoveAnimation = true;
    setTimeout(() => (this.suppressCardMoveAnimation = false), 180);
  }

  // ======================================================================
  // Menu de contexto do card (idêntico em Kanban e Lista — CA08)
  // ======================================================================
  /** Abre a ficha já em edição do título, como a US §2 descreve. */
  editarAssunto(card: OportunidadeCard): void {
    this.abrirCard(card, true);
  }

  excluirAssunto(card: OportunidadeCard): void {
    this.dialog
      .open(ConfirmModalComponent, {
        data: {
          title: 'Excluir assunto',
          description: `O assunto "${card.titulo}" será removido do board. Confirma a exclusão?`,
          buttonConfirm: 'Excluir',
          buttonClose: 'Cancelar',
        },
      })
      .afterClosed()
      .subscribe(result => {
        if (!result?.confirm) return;

        this.oportunidadeService.delete(card.idOportunidade).subscribe({
          next: response => {
            if (response.isError) {
              this.alertService.show(
                'Atenção', response.errorDescription || 'Não foi possível excluir o assunto.', AlertType.warning);
              return;
            }
            this.removerCardDaTela(card);
            this.alertService.show('Sucesso', 'Assunto excluído.', AlertType.success);
          },
          error: () => this.alertService.show('Erro', 'Não foi possível excluir o assunto.', AlertType.error),
        });
      });
  }

  arquivarAssunto(card: OportunidadeCard): void {
    this.alternarArquivo(card, true);
  }

  desarquivarAssunto(card: OportunidadeCard): void {
    this.alternarArquivo(card, false);
  }

  private alternarArquivo(card: OportunidadeCard, arquivar: boolean): void {
    this.oportunidadeService.arquivar(card.idOportunidade, arquivar).subscribe({
      next: response => {
        if (response.isError) {
          this.alertService.show(
            'Atenção', response.errorDescription || 'Não foi possível arquivar o assunto.', AlertType.warning);
          return;
        }

        card.arquivado = arquivar;
        // O card sai da visão atual — Ativas e Arquivadas são conjuntos diferentes.
        this.removerCardDaTela(card);

        this.alertService.show(
          arquivar ? 'Assunto arquivado' : 'Assunto reativado',
          arquivar
            ? 'O card saiu da visão "Ativas" e continua acessível em "Arquivadas".'
            : 'O card voltou para a visão "Ativas".',
          AlertType.success
        );
      },
      error: () => this.alertService.show('Erro', 'Não foi possível arquivar o assunto.', AlertType.error),
    });
  }

  /**
   * Estrela do card e da lista. A estrela troca na hora e volta se a API recusar; na visão Favoritas, desfavoritar
   * tira o card da tela.
   */
  alternarFavorito(card: OportunidadeCard): void {
    if (this.favoritandoIds.has(card.idOportunidade)) return;

    const favoritar = !card.isFavorito;
    card.isFavorito = favoritar;
    this.favoritandoIds.add(card.idOportunidade);

    this.oportunidadeService.favoritar(card.idOportunidade, favoritar).subscribe({
      next: response => {
        this.favoritandoIds.delete(card.idOportunidade);
        if (response.isError) {
          card.isFavorito = !favoritar;
          this.alertService.show(
            'Aviso', response.errorDescription || 'Não foi possível atualizar os favoritos.', AlertType.warning);
          return;
        }

        // Kanban e Lista vêm de buscas separadas: o mesmo card existe nas duas e fica igual nas duas.
        for (const copia of [...Object.values(this.cardsByStage).flat(), ...this.listaCards]) {
          if (copia.idOportunidade === card.idOportunidade) copia.isFavorito = favoritar;
        }

        if (!favoritar && this.visao === 'favoritas') this.removerCardDaTela(card);

        this.alertService.show(
          'Sucesso',
          favoritar ? 'Oportunidade marcada como favorita.' : 'Oportunidade removida dos favoritos.',
          AlertType.success
        );
      },
      error: () => {
        this.favoritandoIds.delete(card.idOportunidade);
        card.isFavorito = !favoritar;
        this.alertService.show(
          'Aviso', 'Não foi possível atualizar os favoritos agora. Tente novamente em alguns instantes.',
          AlertType.warning);
      },
    });
  }

  isFavoritando(card: OportunidadeCard): boolean {
    return this.favoritandoIds.has(card.idOportunidade);
  }

  /** Reabre o card na etapa configurada em "retorno de reabertura" da etapa final (CA16). */
  reabrirOportunidade(card: OportunidadeCard): void {
    const etapaFinal = this.getStage(card.stage);

    // O servidor também valida, mas avisar aqui evita uma ida à API só para levar erro.
    if (!etapaFinal.idEtapaKanbanRetornoReabertura) {
      this.alertService.show(
        'Atenção',
        `A etapa "${etapaFinal.name}" não tem etapa de retorno configurada. Defina-a em Configurações › Vendas › Configuração do Kanban de Oportunidades.`,
        AlertType.warning
      );
      return;
    }

    this.oportunidadeService.reabrir(card.idOportunidade).subscribe({
      next: response => {
        if (response.isError || !response.oportunidade) {
          this.alertService.show(
            'Atenção', response.errorDescription || 'Não foi possível reabrir a oportunidade.', AlertType.warning);
          return;
        }

        this.suppressProgrammaticCardAnimation();
        this.recarregar();
        this.alertService.show(
          'Oportunidade reaberta', `Retornou para ${this.getStage(response.oportunidade.chaveEtapa).name}.`,
          AlertType.success);
      },
      error: () => this.alertService.show('Erro', 'Não foi possível reabrir a oportunidade.', AlertType.error),
    });
  }

  /** A mudança vale para o cadastro do cliente: reflete em todos os cards dele (CA09). */
  /**
   * O status é do cadastro do cliente, em db_pessoa — grava lá e reflete em todos os cards
   * dele (CA09), inclusive nos que não estão carregados.
   */
  alterarStatusCliente(card: OportunidadeCard, status: StatusClienteChave): void {
    if (this.motivoStatusClienteSemPermissao) return;
    const anterior = this.statusPorCliente[card.idCliente];
    const info = this.statusCliente.find(s => s.chave === status);

    this.pessoaService.atualizarStatusCrm(card.idCliente, status).subscribe({
      next: response => {
        if (response.isError) {
          this.statusPorCliente = { ...this.statusPorCliente, [card.idCliente]: anterior };
          this.alertService.show(
            'Atenção', response.errorDescription || 'Não foi possível alterar o status.', AlertType.warning);
          return;
        }
        this.statusPorCliente = { ...this.statusPorCliente, [card.idCliente]: status };
        this.alertService.show(
          'Status atualizado', `${card.cliente} agora é ${info?.label ?? 'atualizado'}.`, AlertType.success);

        // A troca do status já aconteceu na API de Pessoa e não é desfeita por uma falha no
        // registro do histórico: o usuário só é avisado de que o Histórico ficou sem a troca.
        const avisarFalhaHistorico = () => this.alertService.show(
          'Aviso', 'Status alterado, mas não foi possível registrar no Histórico.', AlertType.warning);
        this.oportunidadeService
          .registrarStatusCliente(card.idOportunidade, {
            statusCliente: info?.label ?? 'atualizado',
            statusClienteAnterior: this.statusCliente.find(s => s.chave === anterior)?.label ?? null,
          })
          .subscribe({
            next: response => { if (response.isError) avisarFalhaHistorico(); },
            error: () => avisarFalhaHistorico(),
          });
      },
      error: () => {
        this.statusPorCliente = { ...this.statusPorCliente, [card.idCliente]: anterior };
        this.alertService.show('Erro', 'Não foi possível alterar o status.', AlertType.error);
      },
    });
  }

  statusDoCard(card: OportunidadeCard): StatusClienteInfo {
    const chave = this.statusPorCliente[card.idCliente];
    return this.statusCliente.find(s => s.chave === chave) ?? STATUS_CLIENTE_INDEFINIDO;
  }

  isStatusAtual(card: OportunidadeCard, status: StatusClienteChave): boolean {
    return this.statusDoCard(card).chave === status;
  }

  abrirPainelTags(card: OportunidadeCard): void {
    this.dialog
      .open(TagsOportunidadeDialogComponent, {
        data: {
          titulo: card.titulo,
          disponiveis: this.tagsDisponiveis,
          selecionadas: card.tags.map(t => t.idTagCrm),
        },
        // Mesma largura do "Alterar tags" do Processo de Venda.
        width: '520px',
        autoFocus: false,
        maxWidth: '92vw',
      })
      .afterClosed()
      .subscribe((idsTagCrm?: number[] | null) => {
        if (!idsTagCrm) return;

        this.oportunidadeService.salvarTags(card.idOportunidade, { idsTagCrm }).subscribe({
          next: response => {
            if (response.isError) {
              this.alertService.show(
                'Atenção', response.errorDescription || 'Não foi possível salvar as tags.', AlertType.warning);
              return;
            }
            card.tags = response.tags ?? [];
          },
          error: () => this.alertService.show('Erro', 'Não foi possível salvar as tags.', AlertType.error),
        });
      });
  }

  /** Prioridade do card. Editável na ficha do card; aqui via menu ⋮ enquanto ela não existe. */
  alterarPrioridade(card: OportunidadeCard, prioridade: PrioridadeChave): void {
    const anterior = card.prioridade;
    card.prioridade = prioridade;

    this.oportunidadeService
      .update(card.idOportunidade, { idTipoPrioridade: ID_POR_PRIORIDADE[prioridade] })
      .subscribe({
        next: response => {
          if (response.isError) {
            card.prioridade = anterior;
            this.alertService.show(
              'Atenção', response.errorDescription || 'Não foi possível alterar a prioridade.', AlertType.warning);
            return;
          }
          this.alertService.show(
            'Prioridade atualizada',
            `${card.titulo} — prioridade ${PRIORIDADE_POR_CHAVE[prioridade].label.toLowerCase()}.`,
            AlertType.success
          );
        },
        error: () => {
          card.prioridade = anterior;
          this.alertService.show('Erro', 'Não foi possível alterar a prioridade.', AlertType.error);
        },
      });
  }

  prioridadeDoCard(card: OportunidadeCard): PrioridadeInfo {
    return PRIORIDADE_POR_CHAVE[card.prioridade] ?? PRIORIDADE_POR_CHAVE.media;
  }

  isPrioridadeAtual(card: OportunidadeCard, prioridade: PrioridadeChave): boolean {
    return card.prioridade === prioridade;
  }

  definirProximaAcao(card: OportunidadeCard): void {
    this.dialog
      .open(ProximaAcaoDialogComponent, {
        data: { titulo: card.titulo, descricao: card.proximaAcao, data: card.dataProximaAcao },
        autoFocus: false,
        maxWidth: '92vw',
      })
      .afterClosed()
      .subscribe((result?: ProximaAcaoResult | null) => {
        if (!result) return;

        const idUsuarioResponsavel = this.usuarioResponsavelDaAcao(card);

        this.oportunidadeService
          .salvarProximaAcao(card.idOportunidade, {
            descricao: result.descricao,
            dataPrevista: this.paraIsoDeDataBr(result.data),
            idUsuarioResponsavel,
          })
          .subscribe({
            next: response => {
              if (response.isError) {
                this.alertService.show(
                  'Atenção', response.errorDescription || 'Não foi possível definir a ação.', AlertType.warning);
                return;
              }
              // Kanban e Lista vêm de buscas separadas: o mesmo card existe nas duas e fica igual nas duas.
              for (const copia of [card, ...Object.values(this.cardsByStage).flat(), ...this.listaCards]) {
                if (copia.idOportunidade !== card.idOportunidade) continue;
                copia.proximaAcao = result.descricao;
                copia.dataProximaAcao = result.data;
                copia.idUsuarioResponsavelAcao = idUsuarioResponsavel;
              }
              this.alertService.show('Sucesso', 'Próxima ação definida.', AlertType.success);
            },
            error: () => this.alertService.show('Erro', 'Não foi possível definir a ação.', AlertType.error),
          });
      });
  }

  /** A ação mantém o responsável que já tem; ação nova vai para quem está definindo (o usuário logado). */
  private usuarioResponsavelDaAcao(card: OportunidadeCard): number {
    return card.idUsuarioResponsavelAcao
      ?? this.authService.getUserInfos()?.idUsuario
      ?? 0;
  }

  /** dd/MM/yyyy -> ISO. Os diálogos trabalham no formato brasileiro. */
  private paraIsoDeDataBr(data: string | null): string | null {
    if (!data) return null;
    const partes = data.split('/');
    if (partes.length !== 3) return null;
    return `${partes[2]}-${partes[1]}-${partes[0]}`;
  }

  /**
   * Abre a ficha. Campos por etapa, anexos e comentários só existem no detalhe (o histórico a ficha carrega) —
   * busca antes de abrir, para o modal não montar vazio e preencher depois.
   */
  abrirCard(card: OportunidadeCard, editarTitulo = false): void {
    if (this.idOportunidadeAbrindo !== null) return;

    this.idOportunidadeAbrindo = card.idOportunidade;
    this.oportunidadeService.getById(card.idOportunidade).pipe(
      finalize(() => this.idOportunidadeAbrindo = null),
    ).subscribe({
      next: response => {
        if (response.isError || !response.detalhe) {
          this.alertService.show(
            'Atenção', response.errorDescription || 'Não foi possível abrir a oportunidade.', AlertType.warning);
          return;
        }

        const detalhe = response.detalhe;
        aplicarDetalhe(card, detalhe.campos, detalhe.observacoesFinais);
        // O valor pode ter mudado em Vendas desde a carga do board: a ficha abre com o do detalhe.
        card.valorEstimado = detalhe.oportunidade.valorEstimado ?? 0;
        card.valor = detalhe.oportunidade.valor ?? card.valorEstimado;
        card.idVendaValor = detalhe.oportunidade.idVendaValor ?? null;
        card.vendaTipoDocumentoValor = detalhe.oportunidade.vendaTipoDocumentoValor ?? null;
        card.numeroDocumentoValor = detalhe.oportunidade.numeroDocumentoValor ?? null;
        card.isFavorito = detalhe.oportunidade.isFavorito;
        this.abrirFicha(card, detalhe.anexos, detalhe.comentarios, editarTitulo, detalhe.campos);
      },
      error: () => this.alertService.show('Erro', 'Não foi possível abrir a oportunidade.', AlertType.error),
    });
  }

  private abrirFicha(card: OportunidadeCard, anexos: OportunidadeAnexo[],
    comentarios: OportunidadeComentario[], editarTitulo: boolean,
    campos: OportunidadeCampoValorItem[]): void {
    this.dialog
      .open(OportunidadeCardDialogComponent, {
        data: {
          card,
          stages: this.allStages,
          transicoes: this.transicoes,
          statusCliente: this.statusDoCard(card).chave,
          statusOpcoes: this.statusCliente,
          tagsDisponiveis: this.tagsDisponiveis,
          pessoas: this.peopleList,
          vendedores: this.vendedores,
          idsVendedoresVisiveis: this.idsVendedoresVisiveis,
          resultados: this.lookupResultados,
          motivos: this.lookupMotivos,
          anexos,
          comentarios,
          campos,
          editarTitulo,
          // Campos nativos de Prospecção (Cliente, Contato, Origem, Canal, Valor estimado):
          // os mesmos lookups que a criação já usa.
          origens: this.origens,
          canais: this.lookupCanais,
        },
        panelClass: 'card-edit-panel',
        maxWidth: '100vw',
        maxHeight: '100vh',
        disableClose: true,
        autoFocus: 'dialog',
      })
      .afterClosed()
      .subscribe((resultado?: OportunidadeCardDialogResult | null) => {
        if (!resultado) return;

        if (resultado.acao === 'excluido') {
          this.removerCardDaTela(resultado.card);
          this.alertService.show('Sucesso', 'Assunto excluído.', AlertType.success);
          return;
        }

        // "Outros assuntos": a ficha fechou pedindo para abrir outro card do mesmo cliente.
        // O board recarrega antes, porque a ficha pode ter alterado o card que estava aberto.
        if (resultado.abrirIdOportunidade) {
          this.recarregar();
          this.abrirFichaPorId(resultado.abrirIdOportunidade,
            'Não foi possível abrir o outro assunto.', 'Não foi possível abrir o outro assunto.');
          return;
        }

        // Status do cliente é do cadastro: propaga para todos os cards dele (CA09).
        this.statusPorCliente = {
          ...this.statusPorCliente,
          [resultado.card.idCliente]: resultado.statusCliente,
        };

        if (resultado.acao === 'movido') this.suppressProgrammaticCardAnimation();

        // A ficha escreve direto na API; recarrega para o board refletir o estado real.
        this.recarregar();
      });
  }

  /**
   * Abre a ficha de uma oportunidade que pode não estar na página carregada: monta o card a
   * partir do detalhe, como o board monta a partir da listagem.
   */
  private abrirFichaPorId(idOportunidade: number, mensagemNaoEncontrada: string, mensagemErro: string): void {
    this.oportunidadeService.getById(idOportunidade).subscribe({
      next: resposta => {
        const detalhe = resposta.detalhe;
        if (resposta.isError || !detalhe) {
          this.alertService.show('Aviso', mensagemNaoEncontrada, AlertType.warning);
          return;
        }
        this.comPessoasResolvidas([detalhe.oportunidade], () => {
          const card = mapearCard(detalhe.oportunidade, this.contexto);
          aplicarDetalhe(card, detalhe.campos, detalhe.observacoesFinais);
          this.abrirFicha(card, detalhe.anexos, detalhe.comentarios, false, detalhe.campos);
        });
      },
      error: () => this.alertService.show('Aviso', mensagemErro, AlertType.warning),
    });
  }

  /**
   * "Incluir oportunidade no CRM" do cadastro de Pessoa e dos dialogs de oportunidades do cliente
   * (IA-01 a IA-04). A permissão é conferida aqui, no destino (PA-05): sem Inserir, avisa e não
   * abre o formulário.
   */
  private novaOportunidadeDaPessoa(idPessoa: number, origem: OrigemNovaOportunidade | null): void {
    const usuario = this.authService.getUserInfos();
    if (!usuario?.listaPermissoes?.includes(PermissaoEnum.Vendas_Crm_Inserir)) {
      this.alertService.show(
        'Aviso', 'Você não tem permissão para incluir oportunidades no CRM.', AlertType.warning);
      return;
    }

    // A API de Pessoa exige Visualizar Pessoas: sem ela, o formulário abre sem cliente.
    if (!usuario.listaPermissoes.includes(PermissaoEnum.Cadastros_Pessoas_Visualizar)) {
      this.alertService.show(
        'Aviso',
        'Você não tem permissão para consultar o cadastro de Pessoas. Selecione o cliente no formulário.',
        AlertType.warning
      );
      this.novaOportunidade();
      return;
    }

    this.pessoaService.getById(idPessoa).subscribe({
      next: respostaPessoa => {
        const pessoa = respostaPessoa.pessoa;
        if (respostaPessoa.isError || !pessoa) {
          this.alertService.show(
            'Aviso',
            respostaPessoa.errorDescription || 'Não foi possível carregar a pessoa para iniciar a oportunidade.',
            AlertType.warning
          );
          return;
        }

        // PA-08, como no orçamento e na NF-e: sem o tipo Cliente a busca de cliente do CRM não
        // ofereceria a pessoa; o formulário abre sem cliente, com o aviso.
        if (!(respostaPessoa.listIdTipoContato ?? []).includes(TipoContatoEnum.Cliente)) {
          this.alertService.show(
            'Aviso',
            `Inclua o tipo Cliente no cadastro de ${pessoa.nome} para incluir oportunidades no CRM.`,
            AlertType.warning
          );
          this.novaOportunidade();
          return;
        }

        // RC-04: o vendedor do cadastro da pessoa, se ativo; senão o formulário decide (o próprio
        // vendedor logado ou vazio para escolher).
        const vendedorDaPessoa = pessoa.idVendedor != null ? this.vendedoresPorId.get(pessoa.idVendedor) : undefined;
        const idVendedorInicial = vendedorDaPessoa && vendedorDaPessoa.idSituacaoVendedor !== SituacaoVendedorEnum.Inativo
          ? vendedorDaPessoa.idVendedor
          : null;

        let textoOrigem: string;
        switch (origem) {
          case 'pessoa':
            textoOrigem = `Lançamento iniciado a partir do cadastro de Pessoa para ${pessoa.nome}.`;
            break;
          case 'processo-venda':
            textoOrigem = `Lançamento iniciado a partir do Processo de Venda para ${pessoa.nome}.`;
            break;
          case 'documento-venda':
            textoOrigem = `Lançamento iniciado a partir do documento de venda para ${pessoa.nome}.`;
            break;
          default:
            textoOrigem = `Lançamento iniciado para ${pessoa.nome}.`;
        }

        this.novaOportunidade({
          idPessoaClienteInicial: idPessoa,
          nomeClienteInicial: pessoa.nome,
          idVendedorInicial,
          textoOrigem,
        });
      },
      error: () => this.alertService.show(
        'Aviso', 'Não foi possível carregar a pessoa para iniciar a oportunidade.', AlertType.warning),
    });
  }

  novaOportunidade(inicial?: Pick<NovaOportunidadeData,
    'idPessoaClienteInicial' | 'nomeClienteInicial' | 'idVendedorInicial' | 'textoOrigem'>): void {
    if (!this.escopo) {
      this.alertService.show(
        'Aviso',
        'Não foi possível verificar o seu escopo de visualização. Tente novamente em alguns instantes.',
        AlertType.warning
      );
      return;
    }

    if (!this.allStages.length) {
      this.alertService.show(
        'Atenção',
        'Configure as etapas do kanban antes de criar oportunidades.',
        AlertType.warning
      );
      return;
    }

    this.dialog
      .open(NovaOportunidadeDialogComponent, {
        data: {
          stages: this.allStages,
          origens: this.origens,
          canais: this.lookupCanais,
          tags: this.tagsDisponiveis,
          vendedores: this.vendedores,
          escopo: this.escopo,
          idsVendedoresVisiveis: this.idsVendedoresVisiveis,
          isRestritoVendedor: this.isRestritoVendedor,
          ...inicial,
        } as NovaOportunidadeData,
        autoFocus: false,
        width: '720px',
        maxWidth: '96vw',
        maxHeight: '92vh',
        disableClose: true,
      })
      .afterClosed()
      .subscribe((resultado?: NovaOportunidadeResult | null) => {
        if (!resultado) return;

        // Recarrega em vez de inserir na mão: o card nasce numa coluna cujo total e soma
        // vêm do servidor, e a visão atual pode nem conter o novo card.
        this.suppressProgrammaticCardAnimation();
        this.recarregar();
      });
  }

  /**
   * CSV das oportunidades visíveis, no mesmo formato do processo-compras (`;` + BOM, para o
   * Excel pt-BR abrir sem pedir importação).
   *
   * Exporta o que está carregado. Com paginação isso é menos que o total do servidor — daí
   * o aviso quando há mais páginas: exportar em silêncio um subconjunto seria pior.
   */
  exportar(): void {
    const cards = this.activeTab === 'lista'
      ? this.listaCards
      : Object.values(this.cardsByStage).flat();

    if (!cards.length) {
      this.alertService.show('Atenção', 'Não há oportunidades para exportar.', AlertType.warning);
      return;
    }

    const cabecalho = [
      '#', 'Assunto', 'Cliente', 'Contato', 'Etapa atual', 'Vendedor responsável',
      'Origem', 'Prioridade', 'Próxima ação', 'Quando', 'Tags', 'Criado em', 'Valor (R$)',
    ];

    const linhas = cards.map(card => [
      card.id,
      card.titulo,
      card.cliente,
      card.contato ?? '',
      this.getStage(card.stage).name,
      card.respName,
      card.origem,
      this.prioridadeDoCard(card).label,
      card.proximaAcao ?? '',
      card.dataProximaAcao ?? '',
      card.tags.map(t => t.descricao).join(', '),
      card.criadoEm,
      card.valor ? this.formatarMoeda(card.valor) : '',
    ]);

    const csv = [cabecalho, ...linhas]
      .map(linha => linha.map(valor => this.escaparCsv(valor)).join(';'))
      .join('\r\n');

    // BOM: sem ele o Excel pt-BR abre a acentuação quebrada. Em escape e não no
    // caractere literal, que fica invisível no editor.
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    const agora = new Date();
    const stamp = `${agora.getFullYear()}${String(agora.getMonth() + 1).padStart(2, '0')}`
      + `${String(agora.getDate()).padStart(2, '0')}`
      + `-${String(agora.getHours()).padStart(2, '0')}${String(agora.getMinutes()).padStart(2, '0')}`;

    link.href = url;
    link.download = `oportunidades-${stamp}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    const total = this.totalCardsVisiveis;
    if (total > cards.length) {
      this.alertService.show(
        'Exportação parcial',
        `${cards.length} de ${total} oportunidade(s) exportada(s) — só o que está carregado. Use "Ver Mais" para incluir o resto.`,
        AlertType.warning
      );
      return;
    }

    this.alertService.show('Sucesso', `${cards.length} oportunidade(s) exportada(s).`, AlertType.success);
  }

  private escaparCsv(valor: string): string {
    const texto = (valor ?? '').toString();
    return /[";\r\n]/.test(texto) ? `"${texto.replace(/"/g, '""')}"` : texto;
  }

  // ======================================================================
  // Apresentação do card
  // ======================================================================
  /** Ação vencida: borda esquerda vermelha e data em vermelho com alerta (CA07). */
  isAcaoVencida(card: OportunidadeCard): boolean {
    const data = this.paraData(card.dataProximaAcao);
    if (!data) return false;

    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    return data.getTime() < hoje.getTime();
  }

  /** Valor do card e da coluna Valor da lista: o do documento que o define ou o estimado. */
  valorLabel(card: OportunidadeCard): string {
    return card.valor ? `R$ ${this.formatarMoeda(card.valor)}` : '—';
  }

  valorOrigem(card: OportunidadeCard): string {
    return origemDoValor(card);
  }

  valorIcone(card: OportunidadeCard): string {
    return iconeDoValor(card);
  }

  private formatarMoeda(valor: number): string {
    return Number(valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  private formatarData(data: Date): string {
    return data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  private paraData(texto: string | null): Date | null {
    if (!texto) return null;
    const [d, m, a] = texto.split('/').map(n => parseInt(n, 10));
    if (!d || !m || !a) return null;
    return new Date(a, m - 1, d);
  }

  // ======================================================================
  // Abas
  // ======================================================================
  selecionarAba(tab: 'kanban' | 'lista'): void {
    if (this.activeTab === tab) return;
    this.activeTab = tab;
    setTimeout(() => this.posicionarIndicadorAbas());

    // Cada visão tem sua própria paginação: a Lista sempre recarrega; o Kanban mantém as páginas
    // abertas e só recarrega se a visão ou os filtros mudaram enquanto a Lista estava aberta.
    if (tab === 'lista') this.carregarLista();
    else if (JSON.stringify(this.montarFiltros()) !== this.filtrosDoKanban) this.carregarKanban();
  }

  @HostListener('window:resize')
  posicionarIndicadorAbas(): void {
    const botao = (this.activeTab === 'lista' ? this.tabBtnLista : this.tabBtnKanban)?.nativeElement;
    if (!botao) return;
    this.indicadorX = botao.offsetLeft;
    this.indicadorW = botao.offsetWidth;
  }

  toggleKanbanExpand(): void {
    this.kanbanExpanded = !this.kanbanExpanded;
  }

  // ======================================================================
  // Lista
  // ======================================================================
  onListSortChange(sort: Sort): void {
    this.listSortActive = sort.active;
    this.listSortDirection = sort.direction;
    this.ordenarLista();
  }

  /**
   * Ordenação da Lista é local, sobre as páginas já carregadas — o endpoint ordena por id
   * decrescente e não aceita coluna de ordenação. Com "Ver Mais" a ordem se refaz a cada
   * página; ordenação no servidor precisaria de parâmetro novo na API.
   */
  private ordenarLista(): void {
    const dir = this.listSortDirection === 'asc' ? 1 : this.listSortDirection === 'desc' ? -1 : 0;
    if (!this.listSortActive || dir === 0) return;

    this.listaCards = [...this.listaCards].sort(
      (a, b) => dir * this.compararColunaLista(a, b, this.listSortActive)
    );
  }

  private compararColunaLista(a: OportunidadeCard, b: OportunidadeCard, coluna: string): number {
    switch (coluna) {
      case 'cliente':
        return compararValoresOrdenacao(a.cliente, b.cliente);
      case 'assunto':
        return compararValoresOrdenacao(a.titulo, b.titulo);
      case 'etapa':
        return this.getStage(a.stage).ordem - this.getStage(b.stage).ordem;
      case 'responsavel':
        return compararValoresOrdenacao(a.respName, b.respName);
      case 'proximaAcao':
        return compararValoresOrdenacao(a.proximaAcao ?? '', b.proximaAcao ?? '');
      case 'quando':
        return (this.paraData(a.dataProximaAcao)?.getTime() ?? 0) - (this.paraData(b.dataProximaAcao)?.getTime() ?? 0);
      case 'valor':
        return (a.valor || 0) - (b.valor || 0);
      default:
        return 0;
    }
  }
}
